<template>
  <div>
    <header class="vm-page-header">
      <div>
        <h1>Financeiro</h1>
        <span class="vm-page-header__sub">
          Extrato de consultas e recebimentos
        </span>
      </div>
      <div class="vm-page-header__actions">
        <button class="btn btn--outline btn--sm">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Exportar
        </button>
      </div>
    </header>

    <div class="fin-body">
      <router-link :to="{ name: 'FinanceiroDashboard' }" class="backlink">
        ‹ Voltar à visão geral
      </router-link>

      <div class="tabs">
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroDashboard' }"
          exact
        >
          Visão geral
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroExtrato' }"
        >
          Extrato
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          :to="{ name: 'FinanceiroConta' }"
        >
          Conta bancária
        </router-link>
      </div>

      <!-- Filtros -->
      <div class="card fin-filter-card">
        <div class="fin-filter-row">
          <div class="fin-search-wrap">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              class="fin-search-icon"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" stroke="#94A3B8"
stroke-width="2"
/>
              <path
                d="M21 21l-4-4"
                stroke="#94A3B8"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <input
              v-model="search"
              class="input fin-search-input"
              placeholder="Buscar por paciente ou consulta"
              aria-label="Buscar movimentação"
            />
          </div>
          <select
            v-model="periodFilter"
            class="select"
            style="width:auto;height:40px"
            aria-label="Filtrar por período"
          >
            <option value="">Período: todos</option>
            <option
              v-for="opt in periodOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <select
            v-model="statusFilter"
            class="select"
            style="width:auto;height:40px"
            aria-label="Filtrar por status"
          >
            <option value="">Status: todos</option>
            <option value="crédito">Pago</option>
            <option value="pendente">Aguard. pgto.</option>
            <option value="reembolsado">Reembolsado</option>
          </select>
          <select
            v-model="transferFilter"
            class="select"
            style="width:auto;height:40px"
            aria-label="Filtrar por repasse"
          >
            <option value="">Repasse: todos</option>
            <option value="completed">Concluído</option>
            <option value="scheduled">Agendado</option>
            <option value="pending">A repassar</option>
          </select>
        </div>
      </div>

      <!-- Skeleton -->
      <div v-if="isLoading" class="card card--flush">
        <div
          style="padding:var(--space-4) var(--space-5);display:flex;flex-direction:column;gap:var(--space-3)"
        >
          <div v-for="i in 6" :key="i" class="fin-skel-row">
            <div
              class="vm-skel"
              style="width:36px;height:36px;border-radius:50%"
            />
            <div style="flex:1;display:flex;flex-direction:column;gap:5px">
              <div class="vm-skel" style="width:55%;height:13px" />
              <div class="vm-skel" style="width:35%;height:11px" />
            </div>
            <div class="vm-skel" style="width:80px;height:16px" />
            <div
              class="vm-skel"
              style="width:70px;height:22px;border-radius:50px"
            />
          </div>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-else-if="filteredTransactions.length === 0" class="card">
        <div class="vm-empty">
          <div class="vm-empty-ill">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3>Nenhuma movimentação</h3>
          <p>
            {{
              search || statusFilter || periodFilter || transferFilter
                ? "Nenhuma movimentação corresponde ao filtro aplicado."
                : "Suas movimentações aparecerão aqui após as primeiras consultas realizadas."
            }}
          </p>
        </div>
      </div>

      <!-- Tabela -->
      <div v-else class="card card--flush">
        <div style="overflow-x:auto">
          <table class="table fin-extrato-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Paciente</th>
                <th>Consulta</th>
                <th class="num t-right">Valor bruto</th>
                <th class="num t-right">Taxa ({{ platformPercent }}%)</th>
                <th class="num t-right">Líquido</th>
                <th>Status</th>
                <th>Repasse</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(tx, i) in paginatedTransactions"
                :key="i"
                class="tr--link"
                @click="goToConsulta(tx, i)"
              >
                <td class="num" style="white-space:nowrap">
                  <span>{{ datePart(tx.transaction_date) }}</span>
                  <span style="color:var(--color-gray-400)">
                    {{ yearPart(tx.transaction_date) }}
                  </span>
                </td>
                <td>
                  <div class="row" style="gap:var(--space-2)">
                    <div class="avatar avatar--sm">
                      {{
                        tx.patient
                          ? tx.patient.substring(0, 2).toUpperCase()
                          : "?"
                      }}
                    </div>
                    <span class="cell-strong">
                      {{ tx.patient || tx.description || "Movimentação" }}
                    </span>
                  </div>
                </td>
                <td style="color:var(--color-gray-500)">
                  Teleconsulta · Online
                </td>
                <td class="num t-right money">
                  <span v-if="tx.type === 'crédito'">
                    {{ formatBRL(tx.amount / 100) }}
                  </span>
                  <span v-else style="color:var(--color-gray-400)">—</span>
                </td>
                <td class="num t-right" style="color:var(--color-danger)">
                  <span v-if="tx.type === 'crédito'">
                    − {{ formatBRL((tx.amount / 100) * platformFraction) }}
                  </span>
                  <span v-else style="color:var(--color-gray-400)">—</span>
                </td>
                <td class="num t-right">
                  <span v-if="tx.type === 'crédito'" class="money money--pos">
                    {{ formatBRL((tx.amount / 100) * doctorFraction) }}
                  </span>
                  <span v-else style="color:var(--color-gray-400)">
                    R$ 0,00
                  </span>
                </td>
                <td>
                  <span class="badge" :class="statusBadgeClass(tx)">
                    {{ statusLabel(tx) }}
                  </span>
                </td>
                <td>
                  <span class="badge badge--neutral badge--no-dot">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer paginação -->
        <div class="fin-footer">
          <span class="fin-footer__info">
            Mostrando
            <strong>{{ paginationStart }}–{{ paginationEnd }}</strong>
            de
            <strong>{{ filteredTransactions.length }}</strong>
            consultas · líquido no período
            <strong class="money money--pos">
              {{ formatBRL(totalLiquido) }}
            </strong>
          </span>
          <div style="display:flex;gap:var(--space-2)">
            <button
              class="btn btn--ghost btn--sm"
              :disabled="currentPage === 0"
              @click="currentPage--"
              aria-label="Página anterior"
            >
              ‹ Anterior
            </button>
            <button
              class="btn btn--outline btn--sm"
              :disabled="paginationEnd >= filteredTransactions.length"
              @click="currentPage++"
              aria-label="Próxima página"
            >
              Próxima ›
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from "date-fns";
import { pt as locale } from "date-fns/locale";
import { Doctor } from "../services/doctor";

const PAGE_SIZE = 8;

export default {
  name: "FinanceiroExtrato",

  data() {
    return {
      search: "",
      statusFilter: "",
      periodFilter: "",
      transferFilter: "",
      currentPage: 0,
      comissionPercent: 80
    };
  },

  async mounted() {
    await this.$store.dispatch("doctor/getFinancials");
    Doctor.earnings()
      .then(res => {
        this.comissionPercent = res.data.comission_percent;
      })
      .catch(() => {});
  },

  methods: {
    datePart(date) {
      if (!date) return "—";
      try {
        return format(parseISO(date), "dd/MM");
      } catch {
        return "—";
      }
    },
    yearPart(date) {
      if (!date) return "";
      try {
        return "/" + format(parseISO(date), "yy");
      } catch {
        return "";
      }
    },
    parseAmount(str) {
      if (!str) return 0;
      const cleaned = String(str)
        .replace(/[R$\s]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
      return parseFloat(cleaned) || 0;
    },
    formatBRL(val) {
      if (!val) return "R$ 0,00";
      const str = val.toFixed(2).replace(".", ",");
      const parts = str.split(",");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return "R$ " + parts.join(",");
    },
    statusLabel(tx) {
      if (tx.refunded) return "Reembolsado";
      if (tx.type === "crédito") return "Pago";
      return "Aguard. pgto.";
    },
    statusBadgeClass(tx) {
      if (tx.refunded) return "badge--danger";
      if (tx.type === "crédito") return "badge--success";
      return "badge--warning";
    },
    goToConsulta(tx, idx) {
      this.$router.push({
        name: "FinanceiroConsulta",
        params: { id: tx.id ? String(tx.id) : String(idx) }
      });
    }
  },

  computed: {
    doctorFraction() {
      return this.comissionPercent / 100;
    },
    platformFraction() {
      return 1 - this.comissionPercent / 100;
    },
    platformPercent() {
      return 100 - this.comissionPercent;
    },
    isLoading() {
      return this.$store.getters["doctor/loading"];
    },
    transactions() {
      return this.$store.state.doctor.financials?.transactions || [];
    },
    periodOptions() {
      const opts = [];
      const now = new Date();
      for (let i = 0; i < 3; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push({
          value: format(d, "yyyy-MM"),
          label: format(d, "MMMM/yyyy", { locale }).replace(/^\w/, c =>
            c.toUpperCase()
          )
        });
      }
      return opts;
    },
    filteredTransactions() {
      let list = this.transactions;
      if (this.statusFilter === "crédito") {
        list = list.filter(t => t.type === "crédito" && !t.refunded);
      } else if (this.statusFilter === "reembolsado") {
        list = list.filter(t => t.refunded);
      } else if (this.statusFilter === "pendente") {
        list = list.filter(t => t.type !== "crédito" && !t.refunded);
      }
      if (this.periodFilter) {
        list = list.filter(t =>
          t.transaction_date?.startsWith(this.periodFilter)
        );
      }
      if (this.search.trim()) {
        const q = this.search.toLowerCase();
        list = list.filter(
          t =>
            (t.patient && t.patient.toLowerCase().includes(q)) ||
            (t.description && t.description.toLowerCase().includes(q))
        );
      }
      return list;
    },
    totalLiquido() {
      return this.filteredTransactions
        .filter(t => t.type === "crédito" && !t.refunded)
        .reduce(
          (sum, t) => sum + ((t.amount || 0) / 100) * this.doctorFraction,
          0
        );
    },
    paginationStart() {
      return this.filteredTransactions.length === 0
        ? 0
        : this.currentPage * PAGE_SIZE + 1;
    },
    paginationEnd() {
      return Math.min(
        (this.currentPage + 1) * PAGE_SIZE,
        this.filteredTransactions.length
      );
    },
    paginatedTransactions() {
      const start = this.currentPage * PAGE_SIZE;
      return this.filteredTransactions.slice(start, start + PAGE_SIZE);
    }
  },

  watch: {
    search() {
      this.currentPage = 0;
    },
    statusFilter() {
      this.currentPage = 0;
    },
    periodFilter() {
      this.currentPage = 0;
    },
    transferFilter() {
      this.currentPage = 0;
    }
  }
};
</script>

<style lang="scss" scoped>
.fin-body {
  padding: var(--space-6) var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.tabs {
  margin-bottom: 0;
}

.fin-filter-card {
  padding: var(--space-4);
}

.fin-filter-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.fin-search-wrap {
  position: relative;
  flex: 1;
  min-width: 240px;
}

.fin-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.fin-search-input {
  padding-left: 36px !important;
  width: 100%;
}

.fin-extrato-table {
  min-width: 760px;
}

.fin-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-gray-100);
  flex-wrap: wrap;
  gap: var(--space-3);
}

.fin-footer__info {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.fin-skel-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
</style>
